// src/redux/slices/drawers/drawerStackSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ! README: documentation/development/stackable_drawer_system.md

export type DrawerType =
    | "lotAndContainerDetails"
    | "medicationConfigDrawer"
    | "containerLogDrawer"
    | "containerEntryDrawer"
    | "reconciliationDrawer";

export interface IDrawerHistoryInstance {
    drawerHistoryInstanceId: string;
    type: DrawerType;
    state: any;
    zIndex: number;
    parentId?: string;
}

export interface IDrawerStackState {
    activeDrawers: IDrawerHistoryInstance[];
    nextZIndex: number;
}

export const initialStateDrawerStack: IDrawerStackState = {
    activeDrawers: [],
    nextZIndex: 1040,
};

const MAX_DRAWERS = 5;

const drawerStackSlice = createSlice({
    name: "drawerStack",
    initialState: initialStateDrawerStack,
    reducers: {
        addToDrawerStack: (
            state,
            action: PayloadAction<{
                drawerHistoryInstanceId: string;
                type: DrawerType;
                state: any;
                parentId?: string;
            }>,
        ) => {
            const { drawerHistoryInstanceId, type, state: drawerState, parentId } = action.payload;

            // Remove oldest drawer if we're at the limit
            if (state.activeDrawers.length >= MAX_DRAWERS) {
                state.activeDrawers.shift();
            }

            // Simplified structure - no unnecessary nesting
            state.activeDrawers.push({
                drawerHistoryInstanceId,
                type,
                state: drawerState, // Direct drawer state
                zIndex: state.nextZIndex,
                parentId,
            });
            state.nextZIndex += 1;
        },
        updateDrawerState: (
            state,
            action: PayloadAction<{
                drawerHistoryInstanceId: string;
                state: any;
            }>,
        ) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) =>
                    drawer.drawerHistoryInstanceId === action.payload.drawerHistoryInstanceId,
            );
            if (drawerIndex !== -1) {
                // Create a deep copy of the state using JSON parse/stringify
                // This ensures we break all references and get a completely new object
                const stateDeepCopy = JSON.parse(JSON.stringify(action.payload.state));
                // Replace the entire state object
                state.activeDrawers[drawerIndex] = {
                    ...state.activeDrawers[drawerIndex],
                    state: stateDeepCopy,
                };
            }
        },
        removeFromDrawerStack: (state, action: PayloadAction<string>) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) => drawer.drawerHistoryInstanceId === action.payload,
            );
            if (drawerIndex !== -1) {
                state.activeDrawers.splice(drawerIndex, 1);
            }
        },
        bringToFront: (state, action: PayloadAction<string>) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) => drawer.drawerHistoryInstanceId === action.payload,
            );
            if (drawerIndex !== -1) {
                const drawer = state.activeDrawers[drawerIndex];
                state.activeDrawers.splice(drawerIndex, 1);
                state.activeDrawers.push({
                    ...drawer,
                    zIndex: state.nextZIndex,
                });
                state.nextZIndex += 1;
            }
        },
    },
});

export const { addToDrawerStack, removeFromDrawerStack, bringToFront } = drawerStackSlice.actions;
export default drawerStackSlice.reducer;
